// ============================================================================
// Estado compartido de la caja de contingencia (pasos 3c y 3d · oct-2026).
//
// Un solo lugar sabe si la caja está abierta, qué pedidos de contingencia hay
// en este equipo y si se están subiendo. La lista en memoria se actualiza SOLO
// después de que el almacén local confirmó la escritura: la pantalla nunca
// muestra como "guardado en este equipo" algo que todavía no lo está.
//
// Paso 3d: `subirAhora` manda al servidor lo pendiente (ver
// src/lib/subida-contingencia.ts). La dispara SubidaContingencia cuando hay
// conexión.
// ============================================================================
import { useEffect, useState } from 'react';
import {
  listarPedidosContingencia, guardarPedidoContingencia, actualizarPedidoContingencia,
} from '../../lib/almacen-local';
import { pendientesDeSubir, reintentar, type PedidoContingencia } from '../../lib/caja-contingencia';
import { subirPendientes, type ResultadoSubida } from '../../lib/subida-contingencia';
import { api } from '../../api/client';
import { marcarPedidosLocales } from '../hooks/use-conexion';

let abierta = false;
let pedidos: PedidoContingencia[] = [];
let cargados = false;
let subiendo = false;
let ultimaSubida: (ResultadoSubida & { en: number }) | null = null;
const oyentes = new Set<() => void>();

function avisar() {
  marcarPedidosLocales(pendientesDeSubir(pedidos));
  for (const f of Array.from(oyentes)) {
    try { f(); } catch { /* un oyente no debe romper la caja */ }
  }
}

function reflejar(p: PedidoContingencia) {
  const i = pedidos.findIndex(x => x.id === p.id);
  pedidos = i >= 0 ? pedidos.map(x => (x.id === p.id ? p : x)) : [...pedidos, p];
  avisar();
}

/** Lee del almacén local los pedidos de contingencia. Se llama al arrancar la app. */
export async function cargarPedidosLocales(): Promise<void> {
  try {
    pedidos = await listarPedidosContingencia<PedidoContingencia>();
  } catch (e) {
    console.warn('[contingencia] no se pudieron leer los pedidos locales:', e);
  }
  cargados = true;
  avisar();
}

/** Guarda un pedido NUEVO en el almacén local y, SOLO si salió bien, lo refleja en pantalla. */
export async function guardarPedido(p: PedidoContingencia): Promise<void> {
  await guardarPedidoContingencia(p);
  reflejar(p);
}

/**
 * Cambia un pedido que ya está guardado partiendo de su última versión (no de
 * la que tenía la pantalla): la subida puede haberlo tocado entre medio.
 */
export async function actualizarPedido(
  id: string,
  cambiar: (p: PedidoContingencia) => PedidoContingencia,
): Promise<PedidoContingencia> {
  const p = await actualizarPedidoContingencia<PedidoContingencia>(id, cambiar);
  reflejar(p);
  return p;
}

/** "Reintentar subida" de un pedido en revisión: vuelve a la cola con sus mismas llaves. */
export async function reintentarSubida(id: string): Promise<ResultadoSubida | null> {
  await actualizarPedido(id, reintentar);
  return subirAhora();
}

/**
 * Una vuelta de subida. Si ya hay una en curso (en esta pestaña o en otra),
 * no hace nada. Devuelve null si no corrió.
 */
export async function subirAhora(): Promise<ResultadoSubida | null> {
  if (subiendo) return null;
  subiendo = true;
  avisar();
  try {
    const correr = async () => {
      // Otra pestaña pudo haber subido algo: se parte de lo que dice el almacén.
      await cargarPedidosLocales();
      const r = await subirPendientes({
        listar: () => pedidos,
        actualizar: actualizarPedido,
        enviar: (op) => api.postSubida(op.ruta, op.cuerpo, { llave: op.llave, hechoEn: op.hechoEn }),
        ahora: () => new Date().toISOString(),
      });
      ultimaSubida = { ...r, en: Date.now() };
      return r;
    };
    // Una sola subida a la vez entre pestañas, si el navegador lo permite.
    const locks = (typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManager }).locks : undefined);
    if (locks?.request) {
      return await locks.request('salonika-subida-contingencia', { ifAvailable: true }, async (lock) => (lock ? correr() : null));
    }
    return await correr();
  } catch (e) {
    // Una falla del almacén local o algo inesperado: se reintenta en la próxima vuelta.
    console.warn('[contingencia] la subida se cortó:', e);
    return null;
  } finally {
    subiendo = false;
    avisar();
  }
}

export function abrirCaja() { abierta = true; avisar(); }
export function cerrarCaja() { abierta = false; avisar(); }

export function useCaja() {
  const [, setN] = useState(0);
  useEffect(() => {
    const f = () => setN(n => n + 1);
    oyentes.add(f);
    if (!cargados) void cargarPedidosLocales();
    return () => { oyentes.delete(f); };
  }, []);
  return { abierta, pedidos, cargados, subiendo, ultimaSubida };
}
